import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate } from '../../lib/format';
import MultiSelectFilter from '../../components/workspace/MultiSelectFilter';
import { useReportScope } from '../../lib/useReportScope';
import ReportPdfButton, { ExcelIcon, PdfIcon, excelBtnStyle, downloadExcel } from '../../components/workspace/ReportPdfButton';

const REASON_LABELS = {
  salary_increment:    'Salary Increment',
  annual_appraisal:    'Annual Appraisal',
  promotion:           'Promotion',
  disciplinary_action: 'Disciplinary Action',
  others:              'Others',
};

const STATUS_LABELS = {
  pending_employee: 'Employee Pending',
  pending_manager:  'Manager Review',
  pending_approver: 'Final Approval',
  completed:        'Completed',
  rejected:         'Rejected',
};

const STATUS_COLORS = {
  pending_employee: '#f39c12',
  pending_manager:  '#3498db',
  pending_approver: '#9b59b6',
  completed:        '#27ae60',
  rejected:         '#e74c3c',
};

const labelStyle = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#6b7280';
  const label = STATUS_LABELS[status] || status;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: `${color}22`, color,
    }}>
      {label}
    </span>
  );
}

function Th({ children, right }) {
  return (
    <th style={{
      padding: '10px 14px', textAlign: right ? 'right' : 'left',
      fontWeight: 600, color: 'var(--text-secondary)',
      whiteSpace: 'nowrap', fontSize: 12,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  );
}

export default function AppraisalReport() {
  const { appId } = useParams();
  const api = useApi();
  const { me } = useWorkspace();
  const { isAdmin, scopeIds } = useReportScope(appId);

  const [appraisals,     setAppraisals]     = useState([]);
  const [filterMember,   setFilterMember]   = useState([]);
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterReason,   setFilterReason]   = useState('');
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);

  const loadAppraisals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allR, mineR, manageR, approverR] = await Promise.allSettled([
        api.listAppraisals(appId, { view: 'all' }),
        api.listAppraisals(appId),
        api.listAppraisals(appId, { view: 'manage' }),
        api.listAppraisals(appId, { view: 'pending_approver' }),
      ]);
      const seen = new Set();
      const merged = [];
      for (const r of [allR, mineR, manageR, approverR]) {
        if (r.status === 'fulfilled') {
          for (const a of (r.value.items || [])) {
            if (!seen.has(a.id)) { seen.add(a.id); merged.push(a); }
          }
        }
      }
      setAppraisals(merged);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load appraisals');
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => { loadAppraisals(); }, [loadAppraisals]);

  const userName = u => {
    if (!u) return '—';
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—';
  };

  const uniqueMembers = useMemo(() => {
    const seen = new Map();
    appraisals.forEach(a => {
      if (a.employee && a.employee_user_id != null) {
        seen.set(a.employee_user_id, userName(a.employee));
      }
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [appraisals]);

  const filtered = useMemo(() => {
    let list = [...appraisals];
    if (scopeIds) list = list.filter(a => scopeIds.has(String(a.employee_user_id)));
    if (filterMember.length > 0) list = list.filter(a => filterMember.includes(String(a.employee_user_id)));
    if (filterStatus) list = list.filter(a => a.status === filterStatus);
    if (filterReason) list = list.filter(a => a.reason === filterReason);
    return list;
  }, [appraisals, filterMember, filterStatus, filterReason, scopeIds]);

  const summary = useMemo(() => ({
    total:       filtered.length,
    empPending:  filtered.filter(a => a.status === 'pending_employee').length,
    mgrReview:   filtered.filter(a => a.status === 'pending_manager').length,
    finalApprv:  filtered.filter(a => a.status === 'pending_approver').length,
    completed:   filtered.filter(a => a.status === 'completed').length,
    rejected:    filtered.filter(a => a.status === 'rejected').length,
  }), [filtered]);

  const pdfTable = useMemo(() => ({
    headers: ['Employee', 'Role', 'Reason', 'Initiated By', 'Status', 'Review Periods', 'Date'],
    rows: filtered.map(a => [
      userName(a.employee),
      a.role?.title || '—',
      REASON_LABELS[a.reason] || a.reason || '—',
      userName(a.triggered_by),
      STATUS_LABELS[a.status] || a.status,
      (a.included_cycles || []).map(c => `${c.cycle_name} (${c.year})`).join(', ') || '—',
      a.created_at ? formatDate(a.created_at.slice(0, 10)) : '—',
    ]),
  }), [filtered]);

  const pdfCards = useMemo(() => !loading ? [
    { label: 'Total',            value: summary.total,      color: 'var(--text-primary)' },
    { label: 'Employee Pending', value: summary.empPending, color: '#f39c12' },
    { label: 'Manager Review',   value: summary.mgrReview,  color: '#3498db' },
    { label: 'Final Approval',   value: summary.finalApprv, color: '#9b59b6' },
    { label: 'Completed',        value: summary.completed,  color: '#27ae60' },
    { label: 'Rejected',         value: summary.rejected,   color: '#e74c3c' },
  ] : [], [loading, summary]);

  return (
    <div className="tasks-page">
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">Appraisal Report</h1>
          <p className="tl-subtitle">Status and details of all appraisals across the organisation.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => downloadExcel('appraisal-report.xls', 'Appraisal Report', '', pdfTable.headers, pdfTable.rows)}
            disabled={filtered.length === 0}
            style={{ ...excelBtnStyle, opacity: filtered.length === 0 ? 0.4 : 1 }}
          >
            <ExcelIcon /> Excel
          </button>
          <ReportPdfButton
            title="Appraisal Report"
            subtitle=""
            summaryCards={pdfCards}
            table={pdfTable}
            icon={<PdfIcon />}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap',
        marginBottom: 24, padding: '16px 20px',
        background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Member</label>
          <MultiSelectFilter
            options={uniqueMembers.filter(m => !scopeIds || scopeIds.has(String(m.id))).map(m => ({ value: String(m.id), label: m.name }))}
            selected={filterMember}
            onChange={setFilterMember}
            placeholder="All Members"
            minWidth={200}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Reason</label>
          <select
            value={filterReason}
            onChange={e => setFilterReason(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minWidth: 180 }}
          >
            <option value="">All Reasons</option>
            {Object.entries(REASON_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minWidth: 180 }}
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary cards */}
      {!loading && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {pdfCards.map(c => (
            <div key={c.label} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 110,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No appraisals found for the selected filters.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)' }}>
                <Th>Employee</Th>
                <Th>Role</Th>
                <Th>Reason</Th>
                <Th>Initiated By</Th>
                <Th>Status</Th>
                <Th>Review Periods</Th>
                <Th>Date</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => (
                <tr key={a.id} style={{
                  background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text-primary)', maxWidth: 200, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {userName(a.employee)}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', maxWidth: 180, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {a.role?.title || '—'}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {REASON_LABELS[a.reason] || a.reason || '—'}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {userName(a.triggered_by)}
                  </td>
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <StatusBadge status={a.status} />
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', maxWidth: 220, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {(a.included_cycles || []).map(c => `${c.cycle_name} (${c.year})`).join(', ') || '—'}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {a.created_at ? formatDate(a.created_at.slice(0, 10)) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
